import { inject } from '@angular/core';
import { DashboardService, EventService } from '@core/services';
import { Observable } from 'rxjs';
import { EventEntity, SponsoredAdEntity, StatsEntity } from '@core/entities';
import { map } from 'rxjs/operators';
import { plainToClass } from 'class-transformer';
import { DomSanitizer } from '@angular/platform-browser';
import { ImageConversionService } from '@core/services/image-conversion.service';
import { GenerateImageUrlWithToken, GetUserId, isMobileView } from '@core/utils';
import { SectionType } from '@core/enums';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
export class UseDashboard {
  private readonly _dashboardService = inject(DashboardService);
  private readonly _eventService = inject(EventService);

  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _imageConversionService = inject(ImageConversionService);

  getStats(): Observable<StatsEntity> {
    const userId = +GetUserId();
    return this._dashboardService.getStats(userId).pipe(map((response) => plainToClass(StatsEntity, response)));
  }

  getSponsoredAds(): Observable<SponsoredAdEntity[]> {
    const service$ = isMobileView() ? this._dashboardService.getMobileSponsoredAds() : this._dashboardService.getDesktopSponsoredAds();

    return service$.pipe(
      map((response) => {
        if (response) {
          return response.map((item: SponsoredAdEntity) => {
            const transformedItem = plainToClass(SponsoredAdEntity, item);
            if (transformedItem && transformedItem.id) {
              transformedItem.imageUrl = GenerateImageUrlWithToken(transformedItem.id, SectionType.BANNER);
            }
            return transformedItem;
          });
        }
        return null;
      }),
    );
  }

  getEvents(): Observable<EventEntity[]> {
    const service$: Observable<any> = isMobileView() ? this._eventService.getAllEvents() : this._eventService.getDashboardEvents();

    return service$.pipe(untilDestroyed(this)).pipe(
      map((response) => {
        if (response) {
          return response.map((item: any) => {
            const transformedItem = plainToClass(EventEntity, item);
            if (transformedItem && transformedItem.image) {
              transformedItem.imageUrl = GenerateImageUrlWithToken(transformedItem.id, SectionType.EVENT);
            }
            return transformedItem;
          });
        }
        return null;
      }),
    );
  }

  updateBannerStats(bannerId: number): Observable<any> {
    const data = {
      user_id: GetUserId(),
      banner_id: bannerId,
      display_mode: isMobileView() ? 1 : 0,
    };

    return this._dashboardService.updateBannerStats(data);
  }

  // ----------- AUX -------------

  convertBase64ToImage(image: string) {
    if (!image) {
      return null;
    }
    const base64Data = image.substring(20);
    return this._sanitizer.bypassSecurityTrustUrl(this._imageConversionService.convertBase64ToBlobUrl(base64Data)) as string;
  }
}
